import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import apiService from '../services/api';

type CheckoutState =
  | { status: 'loading'; message: string }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string };

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 24; // ~60s

const CheckoutSuccess: React.FC = () => {
  const location = useLocation();
  const [state, setState] = useState<CheckoutState>({
    status: 'loading',
    message: 'Confirming your payment…',
  });

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('session_id') || '';
  }, [location.search]);

  useEffect(() => {
    let isCancelled = false;

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const run = async () => {
      if (!sessionId) {
        setState({ status: 'error', message: 'Missing session_id. Please check your Stripe receipt link and try again.' });
        return;
      }

      setState({ status: 'loading', message: 'Payment received. Finalizing your account…' });

      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        if (isCancelled) return;

        try {
          const result = await apiService.getGroupCoachingCheckoutStatus(sessionId);

          if (result.status === 'paid' && result.bootstrap_token) {
            const { access, refresh, user } = await apiService.bootstrapFromCheckout(result.bootstrap_token);

            localStorage.setItem('access_token', access);
            localStorage.setItem('refresh_token', refresh);

            const nextPath = result.next || '/onboarding-group';

            if (user?.needs_password_setup) {
              setState({ status: 'success', message: 'Success! Redirecting you to set your password…' });
              const email = encodeURIComponent(user.email || '');
              const next = encodeURIComponent(nextPath);
              window.location.assign(`/set-password?email=${email}&next=${next}`);
              return;
            }

            setState({ status: 'success', message: 'Success! Redirecting you to onboarding…' });

            // Full reload so AuthContext re-mounts and picks up tokens.
            window.location.assign(nextPath);
            return;
          }

          if (result.status === 'failed' || result.status === 'canceled') {
            setState({
              status: 'error',
              message: 'We could not confirm your payment. Please try again.',
            });
            return;
          }

          // pending
          setState({ status: 'loading', message: 'Confirming payment with Stripe…' });
        } catch (error) {
          // transient errors - keep polling
          setState({ status: 'loading', message: 'Still confirming payment…' });
        }

        await sleep(POLL_INTERVAL_MS);
      }

      setState({
        status: 'error',
        message: 'This is taking longer than expected. Please try again from the home page, or contact support.',
      });
    };

    run();
    return () => {
      isCancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <h1 className="text-2xl font-bold text-gray-900">Checkout Success</h1>
        <p className="mt-2 text-gray-600">{state.message}</p>

        {state.status === 'loading' && (
          <div className="mt-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-sm text-gray-500">Please don’t close this tab.</span>
          </div>
        )}

        {state.status === 'error' && (
          <div className="mt-6">
            <div className="p-4 rounded-md bg-red-50 text-red-800 border border-red-200">
              {state.message}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Back to Home
              </Link>
              <Link
                to="/checkout/cancel"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                View Cancel Help
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;

